/* ============================================================
   ContextPanel — Proposal 1 (2026-04-25)

   Faded "{Module} context" panel rendered at the bottom of an
   overview when the current module inherits from an upstream
   one (per MODULE_CONTEXT_FROM).

   Causality:
     - Budgets shows Actuals context (variance baselining)
     - Forecasts shows Budgets + Actuals context (baseline + trend)
     - FCC shows Actuals context (knows what it's mutating)
     - Actuals + Synthesis have no upstream inheritance

   Styling: reduced visual weight (smaller fonts, lower opacity,
   tighter padding) so the tab's primary KPIs stay foregrounded.
   Collapsible — state persisted per (currentModule, contextModule)
   pair in localStorage so users can dismiss panels they don't need.
   ============================================================ */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  buildKPIsTimeline, fmtMoney, MODULE_KPI_REGISTRY,
} from '../engine.js';
import ProvenanceChip from './ProvenanceChip.jsx';

const STORAGE_KEY = 'lens-context-panel-collapsed-v1';

/* Mini formatter — the engine ships several metric formats; keep
   the one-liner compact and fall through to a numeric default. */
function fmtKPIValue(value, format) {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (format) {
    case 'currency': return fmtMoney(value, { decimals: 0 });
    case 'pct':      return `${(value * 100).toFixed(1)}%`;
    case 'days':     return `${Math.round(value)}d`;
    case 'months':   return value === Infinity ? '∞mo' : `${value.toFixed(1)}mo`;
    case 'x':        return `${value.toFixed(2)}×`;
    case 'ratio':
    default:         return value.toFixed(2);
  }
}

const TONE_TEXT = {
  good:    'text-accent-green',
  warn:    'text-accent-gold',
  bad:     'text-accent-red',
  neutral: 'text-text',
};

/* Default open. Users dismiss panels they don't want; the
   localStorage map keeps that choice across sessions. */
function loadCollapsedMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function persistCollapsedMap(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* swallow */ }
}

/* Each module's display label + tone hint for the panel header. */
const MODULE_LABEL = {
  actuals:   { label: 'Actuals',   chip: 'ledger',   tip: 'What actually happened — auditable ledger snapshot.' },
  budgets:   { label: 'Budgets',   chip: 'computed', tip: 'Plan meeting reality — recurring schedules + ledger ratios.' },
  forecasts: { label: 'Forecasts', chip: 'forecast', tip: 'Projection from drivers + assumptions over the horizon.' },
  fcc:       { label: 'FCC',       chip: 'computed', tip: 'Sandbox — ledger baseline plus simulations.' },
  synthesis: { label: 'Synthesis', chip: 'computed', tip: 'Cross-module composition.' },
};

const PROVENANCE_FOR_VIEWER = {
  /* Provenance shown on a context KPI is from the upstream module's
     POV. We use the upstream's own MODULE_LABEL.chip kind so the
     viewer immediately sees "this is ledger context" / "this is
     forecast context" without ambiguity. */
};

export default function ContextPanel({ currentModule, contextModule, scenario }) {
  const meta = MODULE_LABEL[contextModule];
  const upstreamRegistry = MODULE_KPI_REGISTRY[contextModule] || [];

  const dismissKey = `${currentModule}<-${contextModule}`;
  const [collapsed, setCollapsed] = useState(() => !!loadCollapsedMap()[dismissKey]);

  /* Bug fix 2026-04-25 — keep localStorage side-effects OUT of the state
     updater. React strict mode replays updaters in dev, so a write-in-
     updater pattern caused the storage to flip-flop and end up empty
     (collapse stuck false on reload). Effect-based persistence is
     idempotent across strict-mode replays. */
  useEffect(() => {
    const map = loadCollapsedMap();
    if (collapsed) map[dismissKey] = true;
    else delete map[dismissKey];
    persistCollapsedMap(map);
  }, [collapsed, dismissKey]);

  const toggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  /* Build the timeline for the upstream module and pick the column
     whose label matches `currentLabel` (the "now" period). That gives
     us a single-period snapshot of every upstream KPI without a
     parallel "build snapshot" path in the engine. */
  const snapshot = useMemo(() => {
    if (collapsed || !meta || upstreamRegistry.length === 0) return null;
    try {
      const tl = buildKPIsTimeline(scenario, contextModule);
      const idx = tl.periodLabels.findIndex((l) => l === tl.currentLabel);
      const colIdx = idx >= 0 ? idx : Math.max(0, tl.periodLabels.length - 1);
      return tl.rows.map((r) => ({
        label:    r.ratio,
        category: r.category,
        format:   r.format,
        tone:     r.tone,
        value:    r.values?.[colIdx] ?? null,
      }));
    } catch (err) {
      // Engine failure on context = render nothing rather than break the host tab
      console.warn('[ContextPanel] timeline build failed:', err?.message || err);
      return null;
    }
  }, [collapsed, scenario, contextModule, meta, upstreamRegistry.length]);

  if (!meta || upstreamRegistry.length === 0) return null;

  const headerCls = 'flex items-center justify-between gap-2 mb-2 px-[0.7rem] py-[0.45rem] bg-surface-el border border-border rounded-sm';
  const wrapCls = 'mt-6 opacity-[0.78] hover:opacity-100 transition-opacity duration-200';
  const gridCls = 'grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-[0.5rem]';
  const cellCls = 'flex flex-col gap-[2px] px-[0.6rem] py-[0.45rem] bg-surface border border-border rounded-sm';
  const labelCls = 'text-[0.65rem] uppercase tracking-[0.06em] text-text-dim leading-tight';
  const valueCls = 'text-[0.92rem] font-semibold tabular-nums leading-tight';
  const subCls = 'text-[0.6rem] text-text-dim italic mt-[1px]';

  return (
    <div className={wrapCls} aria-label={`${meta.label} context for ${currentModule}`}>
      <div className={headerCls}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <strong className="text-[0.78rem] text-text uppercase tracking-[0.06em]">
            {meta.label} context
          </strong>
          <ProvenanceChip kind={meta.chip} tooltip={meta.tip} compact />
          <em className="text-[0.7rem] text-text-dim not-italic">
            {currentModule === 'budgets' && contextModule === 'actuals' && 'past — what variance is measured against'}
            {currentModule === 'forecasts' && contextModule === 'budgets' && 'present baseline — projection starts here'}
            {currentModule === 'forecasts' && contextModule === 'actuals' && 'historical trend — anchors the projection'}
            {currentModule === 'fcc' && contextModule === 'actuals' && 'ledger baseline — what your sandbox is mutating'}
          </em>
        </div>
        <button
          type="button"
          className="bg-transparent border border-border-strong text-text-muted hover:text-text hover:border-accent-gold px-[0.55rem] py-[0.15rem] rounded-sm cursor-pointer text-[0.7rem] font-[inherit]"
          onClick={toggle}
          aria-expanded={!collapsed}
          title={collapsed ? `Show ${meta.label} context` : `Hide ${meta.label} context`}
        >
          {collapsed ? '▸ Show' : '▾ Hide'}
        </button>
      </div>

      {!collapsed && snapshot && (
        <div className={gridCls}>
          {snapshot.map((kpi) => (
            <div key={kpi.label} className={cellCls}>
              <span className={labelCls} title={kpi.label}>{kpi.label}</span>
              <span className={`${valueCls} ${TONE_TEXT[kpi.tone] || TONE_TEXT.neutral}`}>
                {fmtKPIValue(kpi.value, kpi.format)}
              </span>
              {kpi.category && <span className={subCls}>{kpi.category}</span>}
            </div>
          ))}
        </div>
      )}

      {!collapsed && !snapshot && (
        <p className="text-[0.72rem] text-text-dim italic px-[0.7rem]">
          Context unavailable — engine timeline failed for {meta.label}.
        </p>
      )}
    </div>
  );
}
